import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { prefersReducedMotion } from "./use-in-view";

/**
 * Shader de «pixel art» de la landing: dithering ordenado (Bayer 4×4) en
 * bloques de 8 px con la escala azul del tema. Se dibuja en un <canvas> de
 * baja resolución (una celda = un píxel) escalado con `image-rendering:
 * pixelated`.
 *
 * - `arc`: arco abierto (más alto al centro) con pocas partículas que suben.
 * - `dissolve`: azul marino arriba que se disuelve en blanco al pie.
 *
 * Se pausa fuera de pantalla, corre a ~30 fps y respeta
 * prefers-reduced-motion (un solo cuadro estático).
 */

type Variant = "arc" | "dissolve";

const CELL = 8;
const FRAME_MS = 1000 / 30;

const BAYER = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
].map((row) => row.map((v) => (v + 0.5) / 16));

/** #RRGGBB → entero ABGR (orden de bytes de ImageData en little endian). */
function rgba(hex: string, alpha = 255): number {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return ((alpha << 24) | (b << 16) | (g << 8) | r) >>> 0;
}

const CLEAR = 0;
const ARC_PALETTE = [
  CLEAR,
  rgba("#EFF6FF"),
  rgba("#BEDBFF"),
  rgba("#51A2FF"),
  rgba("#155DFC"),
  rgba("#1C398E"),
];
const DISSOLVE_PALETTE = [
  rgba("#FFFFFF"),
  rgba("#BEDBFF"),
  rgba("#51A2FF"),
  rgba("#155DFC"),
  rgba("#1C398E"),
  rgba("#162456"),
  rgba("#001434"),
];
const NAVY_STAR = rgba("#0B1E48");
const SPARK_NEAR = rgba("#51A2FF");
const SPARK_FAR = rgba("#BEDBFF");

/** Hash entero → [0, 1). Determinista para que el borde no «hierva». */
function hash(a: number, b: number, c: number): number {
  let h = (a * 374761393 + b * 668265263 + c * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** Ruido de valor en el tiempo: cada celda cambia suave, no salta. */
function noise(c: number, r: number, t: number): number {
  const k = Math.floor(t);
  const f = t - k;
  const s = f * f * (3 - 2 * f);
  return hash(c, r, k) * (1 - s) + hash(c, r, k + 1) * s;
}

function quantize(
  palette: number[],
  value: number,
  r: number,
  c: number,
): number {
  const f = Math.max(
    0,
    Math.min(palette.length - 1.0001, value * (palette.length - 1)),
  );
  const i = Math.floor(f);
  return palette[f - i > BAYER[r & 3][c & 3] ? i + 1 : i];
}

type Particle = { c: number; d: number; speed: number };

/** Centro y medio ancho del arco para la columna x ∈ [0, 1]. */
function arcAt(x: number, t: number) {
  const center = 0.62 - 0.22 * (1 - Math.pow(2 * x - 1, 2));
  const half = 0.15 + 0.02 * Math.sin(x * 11 + t * 0.35);
  return { center, half };
}

function drawArc(
  px: Uint32Array,
  cols: number,
  rows: number,
  t: number,
  particles: Particle[],
) {
  px.fill(CLEAR);
  for (let c = 0; c < cols; c++) {
    const x = c / Math.max(1, cols - 1);
    const { center, half } = arcAt(x, t);
    const r0 = Math.max(0, Math.floor((center - half) * (rows - 1)));
    const r1 = Math.min(rows - 1, Math.ceil((center + half) * (rows - 1)));
    for (let r = r0; r <= r1; r++) {
      const y = r / (rows - 1);
      const d = 1 - Math.abs(y - center) / half;
      if (d <= 0) continue;
      const v = Math.pow(d, 1.25) + (noise(c, r, t * 0.6) - 0.5) * 0.1;
      px[r * cols + c] = quantize(ARC_PALETTE, v, r, c);
    }
  }
  // Partículas: se desprenden del borde superior y suben despacio.
  for (const p of particles) {
    if (p.c >= cols) continue;
    const { center, half } = arcAt(p.c / Math.max(1, cols - 1), t);
    const y = center - half - 0.02 - p.d;
    if (y < 0.02) continue;
    const r = Math.floor(y * (rows - 1));
    px[r * cols + p.c] = p.d < 0.08 ? SPARK_NEAR : SPARK_FAR;
    if (p.d < 0.1 && r + 1 < rows && p.speed > 0.03) {
      px[(r + 1) * cols + p.c] = SPARK_FAR;
    }
  }
}

function drawDissolve(
  px: Uint32Array,
  cols: number,
  rows: number,
  t: number,
  bandCells = 34,
) {
  for (let c = 0; c < cols; c++) {
    const x = c / Math.max(1, cols - 1);
    // Banda de disolución de alto fijo (~34 celdas = 272 px) al pie, para que
    // se vea igual en escritorio y en móvil.
    const band = Math.min(rows * 0.6, bandCells);
    const s =
      1 -
      band / Math.max(1, rows - 1) +
      0.03 * Math.sin(x * 8 + t * 0.3) * (band / rows);
    for (let r = 0; r < rows; r++) {
      const y = r / Math.max(1, rows - 1);
      let color: number;
      if (y < s) {
        // Cielo azul marino con algunas celdas apenas más claras.
        color =
          noise(c, r, t * 0.25) > 0.93
            ? NAVY_STAR
            : DISSOLVE_PALETTE[DISSOLVE_PALETTE.length - 1];
      } else {
        const v =
          Math.pow((1 - y) / (1 - s), 0.85) +
          (noise(c, r, t * 0.5) - 0.5) * 0.08;
        color = quantize(DISSOLVE_PALETTE, v, r, c);
      }
      px[r * cols + c] = color;
    }
  }
}

function makeParticles(cols: number): Particle[] {
  const n = Math.max(6, Math.round(cols * 0.2));
  return Array.from({ length: n }, () => ({
    c: Math.floor(Math.random() * cols),
    d: Math.pow(Math.random(), 1.6) * 0.48,
    speed: 0.012 + Math.random() * 0.03,
  }));
}

export function DitherShader({
  variant,
  band,
  className,
}: {
  variant: Variant;
  /** dissolve: alto de la banda de disolución en celdas de 8 px (34). */
  band?: number;
  className?: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!wrap || !canvas || !ctx) return;

    const reduced = prefersReducedMotion();
    let cols = 0;
    let rows = 0;
    let image: ImageData | null = null;
    let pixels: Uint32Array | null = null;
    let particles: Particle[] = [];
    let raf = 0;
    let visible = false;
    let last = 0;
    let time = 0;

    const render = (dt: number) => {
      if (!image || !pixels) return;
      time += dt;
      if (variant === "arc") {
        for (const p of particles) {
          p.d += p.speed * dt;
          if (p.d > 0.5) {
            p.c = Math.floor(Math.random() * cols);
            p.d = Math.random() * 0.03;
            p.speed = 0.012 + Math.random() * 0.03;
          }
        }
        drawArc(pixels, cols, rows, time, particles);
      } else {
        drawDissolve(pixels, cols, rows, time, band);
      }
      ctx.putImageData(image, 0, 0);
    };

    const resize = () => {
      const { width, height } = wrap.getBoundingClientRect();
      const nextCols = Math.max(2, Math.ceil(width / CELL));
      const nextRows = Math.max(2, Math.ceil(height / CELL));
      if (nextCols === cols && nextRows === rows) return;
      cols = nextCols;
      rows = nextRows;
      canvas.width = cols;
      canvas.height = rows;
      canvas.style.width = `${cols * CELL}px`;
      canvas.style.height = `${rows * CELL}px`;
      image = ctx.createImageData(cols, rows);
      pixels = new Uint32Array(image.data.buffer);
      particles = makeParticles(cols);
      render(0);
    };

    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      if (now - last < FRAME_MS) return;
      const dt = last ? Math.min(0.1, (now - last) / 1000) : 0;
      last = now;
      render(dt);
    };

    const start = () => {
      if (reduced || raf) return;
      last = 0;
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      cancelAnimationFrame(raf);
      raf = 0;
    };

    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    resize();

    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) start();
      else stop();
    });
    io.observe(wrap);

    const onVisibility = () => {
      if (document.hidden) stop();
      else if (visible) start();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      ro.disconnect();
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [variant, band]);

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      className={cn("pointer-events-none overflow-hidden", className)}
    >
      <canvas
        ref={canvasRef}
        className="block"
        style={{ imageRendering: "pixelated" }}
      />
    </div>
  );
}
